import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Plus, X, Carrot } from "lucide-react";
import { fetchIngredientList } from "../../api/recipes";
import "./ingredient.css";

/**
 * "What can I make with…" — pick one or more ingredients (with suggestions).
 * Recipes must contain ALL selected ingredients.
 */
const IngredientSearch = ({ selected, onChange }) => {
  const [all, setAll] = useState([]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const listId = useId();

  const ensureLoaded = () => {
    if (all.length === 0) fetchIngredientList().then(setAll).catch(() => {});
  };

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return [];
    const chosen = new Set(selected.map((s) => s.toLowerCase()));
    const starts = all.filter((i) => i.toLowerCase().startsWith(q));
    const contains = all.filter((i) => !i.toLowerCase().startsWith(q) && i.toLowerCase().includes(q));
    return [...starts, ...contains].filter((i) => !chosen.has(i.toLowerCase())).slice(0, 8);
  }, [text, all, selected]);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const add = (name) => {
    if (!name) return;
    onChange([...selected, name]);
    setText("");
    setOpen(false);
    setActive(0);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      add(matches[active] || matches[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && !text && selected.length) {
      onChange(selected.slice(0, -1));
    }
  };

  return (
    <div className="ing" ref={rootRef}>
      <label className="ing-field">
        <Carrot size={16} className="ing-icon" aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={selected.length ? "Add another ingredient…" : "What's in your fridge? e.g. chicken"}
          value={text}
          onFocus={ensureLoaded}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="ing-add"
          onClick={() => add(matches[0])}
          disabled={!matches.length}
          aria-label="Add ingredient"
        >
          <Plus size={16} />
        </button>
      </label>

      {open && matches.length > 0 && (
        <ul className="ing-menu" role="listbox" id={listId}>
          {matches.map((m, i) => (
            <li
              key={m}
              role="option"
              aria-selected={i === active}
              className={i === active ? "active" : ""}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(m)}
            >
              {m}
            </li>
          ))}
        </ul>
      )}

      {selected.length > 0 && (
        <div className="ing-chips" aria-label="Selected ingredients">
          {selected.map((s) => (
            <span key={s} className="ing-chip">
              {s}
              <button onClick={() => onChange(selected.filter((x) => x !== s))} aria-label={`Remove ${s}`}>
                <X size={13} />
              </button>
            </span>
          ))}
          <button className="ing-clear" onClick={() => onChange([])}>Clear all</button>
        </div>
      )}
    </div>
  );
};

export default IngredientSearch;
